import {
  ColDef,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  Module,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  heliosColumnTypes,
  heliosMultiRowSelectionConfig,
  heliosServerSideRowConfig,
} from '@helios/ag-grid-config'
import {
  CommentaryCountsService,
  CommentaryLiveAccountsService,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryGridSelectionService,
  CommentCountCellRenderer,
  commentCountRendererKey,
  COMMENTARY_COLUMN_ID,
  getCommentaryColumnIfAccessible,
  wireCommentaryCounts,
  refilterOnThreadChanges,
  wireCommentaryGrid,
  accountCounts,
} from '@helios/comments'
import { FilterModalService, RevertFilterService } from '@helios/filter-modal'
import {
  Account,
  AccountsRequestContext,
  AdvancedFilterContext,
  AggregateAccount,
  aggregateAccountColDefs,
  getAggregateAccountRowCountRequest,
  HighlightingService,
} from '@helios/generic-components'
import {
  handleColumnVisibilityChange,
  toHeliosRequest,
} from '@helios/server-side-util'
import {
  BasicAccountInfo,
  MyAccountInfo,
  RootState,
  selectFocusedMyAccount,
  selectUserPolicy,
  setFocusedMyAccount,
  userHasCommentaryAccess,
  UserPolicy,
} from '@helios/state-management'
import { getYesterday } from '@helios/utils'
import { Store } from '@ngrx/store'
import { composeFieldPath, formatDate, ISODateFormat } from '@northfork/basics'
import { Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  shareReplay,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { createFilterContextFromQuickSearch } from '../quick-search/quick-search-utils'
import { HttpAccountListService } from '../services/http-account-list.service'
import { AccountListGridService } from '../services/account-list-grid.service'
import {
  AccountListStatusBarModule,
  accountListStatusBarPanel,
} from '../status-bar/external-accounts-status-bar.module'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { ExternalAccountsPanelKey } from '../../panel-key'
import { externalAccountCommentary } from '../external-accounts-commentary'

const accountPath = composeFieldPath([] as Account[])
const aggregateAccountListGridKey = `${ExternalAccountsPanelKey}-AggGrid`

@Component({
  selector: 'hs-aggregate-account-list-grid',
  templateUrl: './aggregate-account-list-grid.component.html',
  styleUrls: ['./aggregate-account-list-grid.component.scss'],
  providers: [FilterModalService, HighlightingService, RevertFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AggregateAccountListGridComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input()
  set actualReconDate(dateString: string | null) {
    if (dateString == null) {
      return
    }
    this.advancedFilterContext.reconDate = dateString
  }

  advancedFilterContext: AdvancedFilterContext = {
    advancedFilters: {},
    reconDate: formatDate(getYesterday(), ISODateFormat) ?? '', // since getYesterday always returns date value so recon date won't be blank
  }

  private readonly destroySubject$ = new Subject<void>()

  readonly gridOptions$ = this.createGridOptions()

  quickSearch = new FormControl('')

  api?: GridApi
  columnApi?: ColumnApi
  focusedAggregateAccount?: MyAccountInfo

  hasAccessCommentary = false
  private commentaryWired = false

  readonly gridModules: Module[] = [
    MasterDetailModule,
    FiltersToolPanelModule,
    ServerSideRowModelModule,
    RangeSelectionModule,
    AccountListStatusBarModule,
  ]

  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly accountListService: HttpAccountListService,
    private readonly accountListGridService: AccountListGridService,
    private readonly store: Store<RootState>,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly commentaryCountsService: CommentaryCountsService,
    private readonly commentaryLiveAccounts: CommentaryLiveAccountsService,
    private readonly commentaryFollowService: CommentaryFollowService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {
    this.store
      .select(selectUserPolicy)
      .pipe(
        filter(policy => policy != undefined),
        take(1),
      )
      .subscribe(userPolicy => this.checkCommentaryAccess(userPolicy))
  }

  /**
   * DLITE-3912 — mirror the Accounts panel's commentary integration. Wire this
   * grid into the commentary drawer once the user has commentary access.
   * @internal
   */
  checkCommentaryAccess(userPolicy?: UserPolicy) {
    if (userPolicy && userHasCommentaryAccess(userPolicy)) {
      this.hasAccessCommentary = true
      this.wireCommentary()
    }
  }

  /**
   * Wire the aggregates grid into the three commentary integration points:
   * follow-a-chip row focus, entity-link type-ahead, and Pick-from-table. The
   * row-node id is `accountId.toString()` (see getRowNodeId), so focusGridRow
   * looks the node up directly. `filterWhenMissing: false` because an account
   * chip is ambiguous — it may belong to the Accounts panel or the sibling
   * sub-account grid — so this grid only focuses rows it already holds rather
   * than filtering itself to an empty result. Idempotent; torn down via
   * destroySubject$.
   */
  private wireCommentary() {
    if (this.commentaryWired) return
    this.commentaryWired = true
    wireCommentaryGrid({
      follow: this.commentaryFollowService,
      search: this.entitySearchService,
      selection: this.gridSelectionService,
      panelKey: aggregateAccountListGridKey,
      entityType: 'account',
      idColId: 'accountId',
      getApi: () => this.api,
      hasAccess: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      loadedRefs: () => this.collectLoadedAccountRefs(),
      selectedRefs: () => this.collectSelectedAccountRefs(),
      filterWhenMissing: false,
    })
  }

  private collectLoadedAccountRefs(): CommentaryEntityRef[] {
    const refs: CommentaryEntityRef[] = []
    this.api?.forEachNode(node => {
      const id = (node.data as Account)?.accountId
      if (id != null) refs.push({ type: 'account', id: id.toString() })
    })
    return refs
  }

  private collectSelectedAccountRefs(): CommentaryEntityRef[] {
    return (this.api?.getSelectedNodes() ?? [])
      .map(node => (node.data as Account)?.accountId)
      .filter((id): id is number => id != null)
      .map(id => ({ type: 'account' as const, id: id.toString() }))
  }

  ngOnInit() {
    this.quickSearch.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
      )
      .subscribe(() => {
        this.advancedFilterContext = createFilterContextFromQuickSearch(
          this.quickSearch.value,
          this.advancedFilterContext,
        )
        this.api?.onFilterChanged()
      })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.api?.destroy()
  }

  ngAfterViewInit(): void {
    this.store
      .select(selectFocusedMyAccount)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(account => {
        this.focusedAggregateAccount = account
        this.api?.redrawRows()
      })
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        try {
          const request = toHeliosRequest<
            AggregateAccount,
            AccountsRequestContext
          >(params, [], externalAccountCommentary.mandatoryFields(), {
            filterContext: {
              ...this.advancedFilterContext,
            },
          })

          this.accountListService
            .getAggregateAccounts(request)
            .pipe(
              take(1),
              takeUntil(this.destroySubject$),
              tap(accounts => {
                const totalRows =
                  (params?.request?.startRow ?? 0) + accounts.length
                params.success({
                  rowData: accounts,
                  rowCount:
                    totalRows < (params?.request?.endRow ?? totalRows)
                      ? totalRows
                      : undefined,
                })
              }),
              tap(() => {}),
            )
            .subscribe()
        } catch (e) {
          params.fail()
        }
      },
    }
  }

  private async createColumnDefs(): Promise<ColDef[]> {
    // DLITE-3912: pinned-left commentary column; click opens the drawer
    // scoped to that account. Omitted entirely for users without commentary
    // access — see getCommentaryColumnIfAccessible.
    const commentaryColDefs = await getCommentaryColumnIfAccessible(
      this.store,
      externalAccountCommentary,
      () => this.hasAccessCommentary,
      this.commentaryCountsService,
      accountCounts,
    )
    // Presence for the aggregate's OWN accountId only, not a rollup across subaccounts —
    // that would mean loading every subaccount's counts. hideBadge drops the number, and
    // the tooltips flag that outline doesn't mean "no comments anywhere", since the drawer
    // itself does roll subaccounts up.
    const commentsColDef = commentaryColDefs.find(
      c => c.colId === COMMENTARY_COLUMN_ID,
    )
    if (commentsColDef) {
      commentsColDef.cellRendererParams = { hideBadge: true }
      commentsColDef.headerTooltip =
        'Shows whether the aggregate itself has a comment. Comments on its underlying simple accounts still appear when you open the drawer.'
      commentsColDef.tooltipValueGetter = () =>
        'Reflects comments on this aggregate itself — open the drawer to see comments on its underlying simple accounts too.'
    }
    return [
      ...commentaryColDefs,
      ...aggregateAccountColDefs(accountPath, this.getAccountsSetValues()),
    ]
  }

  private async createGridOptions(): Promise<GridOptions> {
    return {
      columnDefs: await this.createColumnDefs(),
      components: {
        [commentCountRendererKey]: CommentCountCellRenderer,
      },
      // DLITE-3912: stable row-node id lets the commentary chip-follow focus a
      // loaded account row directly via api.getRowNode(accountId).
      getRowNodeId: ({ accountId }: Account) => accountId.toString(),
      ...heliosServerSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      suppressContextMenu: true,
      preventDefaultOnContextMenu: true,
      rowClassRules: {
        'loaded-account': params =>
          params.data?.accountId !== undefined &&
          this.focusedAggregateAccount?.accountId !== undefined &&
          params.data.accountId === this.focusedAggregateAccount.accountId,
      },
      columnTypes: {
        ...heliosColumnTypes,
      },
      serverSideDatasource: this.createDatasource(),
      statusBar: accountListStatusBarPanel,
      onGridReady: event => this.onGridReady(event),
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'AggregateAccountList'),
      context: {
        resolver: this.resolver,
        changeDetector: this.changeDetector,
        getRowCountRequest: (api: GridApi) =>
          getAggregateAccountRowCountRequest(api, this.advancedFilterContext),
      },
      onRowDoubleClicked: event => {
        const account = event.data as BasicAccountInfo
        const myAccount: MyAccountInfo = {
          accountId: account.accountId,
          accountName: account.accountName,
          isAggregate: true,
        }
        this.store.dispatch(
          setFocusedMyAccount({
            account: myAccount,
          }),
        )
      },
      onRowClicked: event => {
        const account = event.data as BasicAccountInfo
        this.accountListGridService.setSelectedAggregateForFilter(account)
      },
    }
  }

  /**
   * @internal
   */
  onGridReady(event: GridReadyEvent) {
    const { api } = event

    this.api = api
    this.columnApi = event.columnApi

    wireCommentaryCounts<Account>({
      changes$: this.commentaryCountsService.changes$,
      getApi: () => api,
      hasAccessForLoad: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      accountIdOf: row => row?.accountId,
      loadCounts: ids => this.commentaryCountsService.ensureAccountsLoaded(ids),
      live: { accounts: this.commentaryLiveAccounts, surface: 'total' },
    })
    refilterOnThreadChanges<Account>(() => api, this.destroySubject$, {
      accountIdOf: row => row?.accountId,
    })

    enableHeliosMultiRowDragSelection(api)
    enableHeliosCopyBehavior(api)

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(api, DensityLevel.Compact === level),
      )

    this.savedViewService.manageSavedView(
      event,
      aggregateAccountListGridKey,
      this.destroySubject$,
      true,
    )
  }

  private getAccountsSetValues() {
    return this.accountListService
      .getAccountsSetValues()
      .pipe(take(1), takeUntil(this.destroySubject$), shareReplay(2))
  }
}
